// The arithmetic behind the conversion progress view.
//
// 🚩 Split out of `image-progress.tsx` for one reason: Node strips types from `.ts` but cannot parse
// `.tsx`, so a number-producing function that lives beside JSX is a function with no unit test.

export type Phase = 'queued' | 'uploading' | 'converting' | 'done' | 'kept';

export interface FileProgress {
  filename: string;
  size: number;
  phase: Phase;
  /** 0..1, bytes sent. Meaningful during `uploading` only. */
  uploaded: number;
  /** Bytes after conversion, once known. */
  finalSize?: number;
}

/**
 * How far through its own unit of work one file is, 0..1.
 *
 * 🔴 The upload is the first 70% and the server's work the rest. That split is PRESENTATIONAL and
 * not measured — the two legs cannot be compared without knowing the network. It exists so the ring
 * advances smoothly instead of jumping a whole file at a time. Nothing depends on it being right.
 * 🚩 What IS honest is the upload number itself: it comes from `xhr.upload.onprogress`, real bytes
 * sent. The server reports nothing about its own work, so that leg never claims a percentage.
 */
export const UPLOAD_SHARE = 0.7;

export function fileFraction(f: FileProgress): number {
  if (f.phase === 'done' || f.phase === 'kept') return 1;
  if (f.phase === 'converting') return UPLOAD_SHARE;
  if (f.phase === 'uploading') return Math.max(0, Math.min(1, f.uploaded)) * UPLOAD_SHARE;
  return 0;
}

/** Overall completion across every file; each file is worth one equal unit. */
export function overallFraction(files: FileProgress[]): number {
  if (!files.length) return 0;
  return Math.min(1, files.reduce((acc, f) => acc + fileFraction(f), 0) / files.length);
}

/** The percentage shown on a single file's row. */
export function filePercent(f: FileProgress): number {
  if (f.phase === 'queued') return 0;
  if (f.phase === 'uploading') return Math.round(Math.max(0, Math.min(1, f.uploaded)) * 100);
  return 100;   // converting, done and kept all show a full bar; the phase label carries the rest
}

/** `−62%`, or null when the conversion did not actually save anything. */
export function savedLabel(f: FileProgress): string | null {
  if (f.finalSize == null || f.size <= 0 || f.finalSize >= f.size) return null;
  return `−${Math.round((1 - f.finalSize / f.size) * 100)}%`;
}

/** Keep the extension when shortening: `long-holiday-photo.heic` → `long-holiday…heic`. */
export function shortName(name: string, max = 28): string {
  const n = String(name ?? '');
  if (n.length <= max) return n;
  const dot = n.lastIndexOf('.');
  const ext = dot > 0 ? n.slice(dot) : '';
  return n.slice(0, Math.max(1, max - ext.length - 1)) + '…' + ext;
}
